import type { MemberHra, RecommendationReview } from '@troyai/hra/data-access';
import type {
  HealthStoryCategoryOption,
  HealthStoryEntry,
  HealthStoryGroup,
  HealthStoryPage,
  MedicalVisit,
  MedicalVisitPage,
  TimelineViewResponse,
} from '@troyai/portal/health-timeline/data-access';
import type { EpisodeSummary } from '@troyai/portal/intervention-episodes/data-access';
import { format, parseISO } from 'date-fns';

import type { MergeKind } from './demo-classify';

/**
 * Merges the real half of a list with the scripted half. Pure: no input is changed.
 *
 * Both halves arrive as `unknown`, from the network or the store, so each is checked for the
 * shape its kind needs. A real half that does not have it (an error body, say) counts as empty
 * and the scripted half is answered alone.
 */

/** Calls & notes exists only in the demo; the backend does not list it among the categories. */
const INTERACTION_CATEGORY: HealthStoryCategoryOption = {
  key: 'interaction',
  label: 'Calls & notes',
};

// ---- Shape guards ----------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** The value as a `T` when it is a record holding a list under `field`, else null. */
function withList<T>(value: unknown, field: keyof T & string): T | null {
  return isRecord(value) && Array.isArray(value[field]) ? (value as T) : null;
}

/** Milliseconds for a date or instant, so the two formats compare; unreadable sorts oldest. */
function timeOf(value: unknown): number {
  const time = typeof value === 'string' ? Date.parse(value) : NaN;
  return Number.isNaN(time) ? -Infinity : time;
}

/** Newest first by `field`; the sort is stable, so equal dates keep their order (real first). */
function newestFirst<T>(items: T[], field: (item: T) => unknown): T[] {
  return [...items].sort((a, b) => {
    const diff = timeOf(field(b)) - timeOf(field(a));
    return Number.isNaN(diff) ? 0 : Math.sign(diff);
  });
}

// ---- The health story ------------------------------------------------------------------------

function storyEntries(page: { groups: HealthStoryGroup[] }): HealthStoryEntry[] {
  return page.groups.flatMap((group) => listOf<HealthStoryEntry>(group.entries));
}

function oldestDate(entries: HealthStoryEntry[]): string | null {
  let oldest: string | null = null;
  for (const { date } of entries) {
    if (typeof date === 'string' && (oldest === null || date < oldest)) {
      oldest = date;
    }
  }
  return oldest;
}

/** The oldest entry `date` of a real health-story page; null when it has none. */
export function storyPageOldest(page: unknown): string | null {
  const story = withList<HealthStoryPage>(page, 'groups');
  return story ? oldestDate(storyEntries(story)) : null;
}

/**
 * One real story page with the scripted entries that belong on it. The backend pages the story
 * newest first, 50 entries at a time, and the scripted entries go on the page their date falls
 * in, so each shows up on exactly one page:
 *
 * - below `upperBound`, the oldest entry of the page before (none on the first page), and
 * - from the oldest entry of this page upward, or, on the last page (`has_more` false), all the
 *   way down.
 *
 * Real and scripted entries are put in one list, newest first (real first on a tie), and
 * regrouped by month with the backend's label.
 */
export function mergeStoryPage(
  real: HealthStoryPage,
  scripted: HealthStoryEntry[],
  upperBound: string | null
): HealthStoryPage {
  const realEntries = storyEntries(real);
  const oldestReal = oldestDate(realEntries);

  const taken = scripted.filter(
    (entry) =>
      (upperBound === null || entry.date < upperBound) &&
      (!real.has_more || (oldestReal !== null && entry.date >= oldestReal))
  );

  const groups: HealthStoryGroup[] = [];
  for (const entry of newestFirst([...realEntries, ...taken], (e) => e.date)) {
    const label = format(parseISO(entry.date), 'MMMM yyyy');
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.entries.push(entry);
    } else {
      groups.push({ label, entries: [entry] });
    }
  }

  const categories = listOf<HealthStoryCategoryOption>(real.categories);
  return {
    ...real,
    groups,
    categories: categories.some((category) => category.key === INTERACTION_CATEGORY.key)
      ? categories
      : [...categories, INTERACTION_CATEGORY],
  };
}

// ---- The merge -------------------------------------------------------------------------------

/**
 * The answer of a merge request: the real half and the scripted half combined for `kind`.
 * `storyUpperBound` only matters for `story` (see `mergeStoryPage`).
 */
export function mergeAnswer(
  kind: MergeKind,
  real: unknown,
  scripted: unknown,
  storyUpperBound: string | null
): unknown {
  switch (kind) {
    case 'episodes':
      return [...listOf<EpisodeSummary>(real), ...listOf<EpisodeSummary>(scripted)];
    case 'reviews':
      // The Up next rules read index 0 as the newest review.
      return newestFirst(
        [...listOf<RecommendationReview>(real), ...listOf<RecommendationReview>(scripted)],
        (review) => review.submitted_at
      );
    case 'assessments':
      return newestFirst(
        [...listOf<MemberHra>(real), ...listOf<MemberHra>(scripted)],
        (hra) => hra.submission_date
      );

    case 'signals': {
      const response = withList<TimelineViewResponse>(real, 'events');
      if (!response) {
        return scripted;
      }
      const extra = withList<TimelineViewResponse>(scripted, 'events');
      return { ...response, events: [...response.events, ...(extra?.events ?? [])] };
    }

    case 'visits': {
      const page = withList<MedicalVisitPage>(real, 'visits');
      if (!page) {
        return scripted;
      }
      const extra = withList<MedicalVisitPage>(scripted, 'visits');
      return {
        ...page,
        visits: newestFirst<MedicalVisit>(
          [...page.visits, ...(extra?.visits ?? [])],
          (visit) => visit.date
        ),
      };
    }

    case 'versions':
      return {
        entries: [
          ...listOf<unknown>(withList<{ entries: unknown[] }>(real, 'entries')?.entries),
          ...listOf<unknown>(withList<{ entries: unknown[] }>(scripted, 'entries')?.entries),
        ],
      };

    case 'story': {
      const page = withList<HealthStoryPage>(real, 'groups');
      if (!page) {
        return scripted;
      }
      const extra = withList<HealthStoryPage>(scripted, 'groups');
      return mergeStoryPage(page, extra ? storyEntries(extra) : [], storyUpperBound);
    }
  }
}

/**
 * What an empty real answer looks like, when there is no backend to ask (the scripted fallback
 * member): merged with `scripted` it gives `scripted` back. The records keep the scripted half's
 * other fields; their list is emptied.
 */
export function emptyRealAnswer(kind: MergeKind, scripted: unknown): unknown {
  const base = isRecord(scripted) ? scripted : {};
  switch (kind) {
    case 'episodes':
    case 'assessments':
    case 'reviews':
      return [];
    case 'signals':
      return { ...base, events: [] };
    case 'visits':
      return { ...base, visits: [] };
    case 'versions':
      return { entries: [] };
    case 'story':
      return { ...base, groups: [], has_more: false };
  }
}
